import { Component, OnInit } from '@angular/core';
import { ApiRoutes } from '@sb/library/api';
import { DrawerBaseViewComponent, ErrorMessages, Errors, Field, FormBase, PrimaryActionEvent, Required, Scope, Validators, string } from '@sb/library/base';
import { MfaInputItem, MfaSetupDisplayItem, MfaSetupResult } from '@sb/library/data-models';

class MFACodeForm extends FormBase {
    Code1 = Required(string);
    Code2 = Required(string);
}

class MFAErrorForm extends FormBase {
    AlreadySetup = Required(string);
    IncorrectCode = Required(string);
    DuplicateCode = Required(string);
}
export class MultiFactorAuthSetupDrawerScope extends Scope {
    RefreshAfterSetupMFA = Field<()=>void>();
}

@Component({
    selector: 'lib-multi-factor-auth-setup-mfa-drawer',
    templateUrl: './multi-factor-auth-setup-mfa-drawer.component.html',
    styleUrls: ['./multi-factor-auth-setup-mfa-drawer.component.scss']
})
export class MultiFactorAuthSetupDrawerComponent extends DrawerBaseViewComponent<string> implements OnInit {
    Scope = this.SetScope({
        Requires: MultiFactorAuthSetupDrawerScope
    });

    readonly mfaCodeForm = MFACodeForm.Create();

    readonly mfaErrorForm = MFAErrorForm.Create();
    protected alreadySetupErrors: Errors = new Errors();
    protected incorrectCodeErrors: Errors = new Errors();

    showSecretCode = false;

    readonly mfaErrors: ErrorMessages = {
        required: value => $localize`:@@CommonMFAInputLengthErrorMessage:Please enter a 6-digit code.`,
        exactlength: () => $localize`:@@CommonMFAInputLengthErrorMessage:Please enter a 6-digit code.`,
        api: () => $localize`:@@CommonMFASetupReEnterCodesMessage:Please re-enter your 2-Factor code.`,
        duplicate: _ => $localize`:@@CommonMFASetupDuplicateCodeMessage:2-Factor codes cannot be the same. Please enter two consecutive codes.`
    };

    private _isLoading: boolean = true;
    private _enablingMFA: boolean = false;
    private _mfaSetup!: MfaSetupDisplayItem;

    constructor() {
        super();

        this.alreadySetupErrors.Override({
            api: _ => $localize`:@@CommonMFAAlreadySetupErrorMessage:2-Factor Authentication is already setup. If you wish to setup a new code, please disable 2FA first.`
        });

        this.incorrectCodeErrors.Override({
            api: _ => $localize`:@@CommonMFASetupInvalidCodesErrorMessage:Setting up 2-Factor Authentication failed because the provided codes are incorrect. Please try again.`
        });

        this.mfaCodeForm.controls.Code1.AddValidators([Validators.required, Validators.exactLength(6)]);
        this.mfaCodeForm.controls.Code2.AddValidators([Validators.required, Validators.exactLength(6)]);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.mfaCodeForm.disable();

        ApiRoutes.Authentication.Mfa.Initialize.Call({
            Parameter: this.data
        }).subscribe(data => {
            this.mfaCodeForm.enable();
            this._mfaSetup = data;
            this._isLoading = false;
        });
    }

    override PrimaryAction(): void {
        this.mfaCodeForm.controls.Code2.AddValidators([Validators.required, Validators.exactLength(6), Validators.duplicate([this.mfaCodeForm.controls.Code1.value], false)]);
        this.mfaCodeForm.CheckValidity().subscribe(result => {
            if (result) {
                this.primaryActionEvent.next(PrimaryActionEvent.Clicked);
                this._enablingMFA = true;

                const inputItem = new MfaInputItem({
                    MfaCode1: this.mfaCodeForm.controls.Code1.value.trim(),
                    MfaCode2: this.mfaCodeForm.controls.Code2.value.trim(),
                    Secret: this._mfaSetup.Secret
                });

                ApiRoutes.Authentication.Mfa.Enable.Call({
                    Parameter: this.data,
                    Body: inputItem
                }).subscribe(result => {
                    switch (result) {
                        case MfaSetupResult.Success:
                            this.DismissAction();
                            this.Scope.RefreshAfterSetupMFA();
                            break;
                        case MfaSetupResult.AlreadySetup:
                            this.SetApiError(result);
                            this.EnableForm();
                            break;
                        case MfaSetupResult.IncorrectCode:
                            this.SetApiError(result);
                            this.EnableForm();
                            break;
                    }
                });
            } else {
                this.EnableForm();
                this.mfaErrorForm.enable();
            }
        });
    }

    SetApiError(error: MfaSetupResult): void {
        this.mfaErrorForm.controls.AlreadySetup.setValue("needed");
        this.mfaErrorForm.controls.AlreadySetup.markAsTouched();
        this.mfaErrorForm.controls.AlreadySetup.markAsDirty();

        this.mfaErrorForm.controls.IncorrectCode.setValue("needed");
        this.mfaErrorForm.controls.IncorrectCode.markAsTouched();
        this.mfaErrorForm.controls.IncorrectCode.markAsDirty();

        this.mfaErrorForm.markAllAsTouched();
        this.mfaErrorForm.CheckValidity();
        this.mfaErrorForm.enable();

        switch (error) {
            case MfaSetupResult.Success:
                break;
            case MfaSetupResult.AlreadySetup:
                this.mfaErrorForm.controls.AlreadySetup.SetApiError();
                break;
            case MfaSetupResult.IncorrectCode:
                this.mfaErrorForm.controls.IncorrectCode.SetApiError();
                break;
        }

        //reset the forms
        this.mfaCodeForm.controls.Code1.setValue("");
        this.mfaCodeForm.controls.Code2.setValue("");

        this.mfaCodeForm.controls.Code1.markAsPristine();
        this.mfaCodeForm.controls.Code2.markAsPristine();

        this.mfaCodeForm.controls.Code1.markAsUntouched();
        this.mfaCodeForm.controls.Code2.markAsUntouched();

        this.mfaErrorForm.updateValueAndValidity();
    }

    EnableForm(): void {
        this.mfaCodeForm.enable();
        this._enablingMFA = false;
    }

    AddToAuthenticatorApp(): void {
        window.open(this._mfaSetup.Url!);
    }

    get mfaSetup(): MfaSetupDisplayItem {
        return this._mfaSetup;
    }

    get secretCode(): String {
        return this._mfaSetup.Secret!;
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    get enablingMFA(): boolean {
        return this._enablingMFA;
    }
}
